import React, {useState, useEffect} from 'react';
import {AddEditMemberModal} from 'shared-one-ux-components';
import axios from 'axios';
import find from 'lodash/find';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';

export default function MembersAdd({
	isOpen,
	onClose,
	fetchMembers,
	adminView,
	ooid,
	memberRoles = [],
	setToastData,
	roleDescriptions = [],
	setMembersList,
}) {

	const [irecBillingInfo, setIrecBillingInfo] = useState(null);

	useEffect(() => {
		// Silently pre-fetch iReceivables billing info so peCode can be passed directly
		// to the Step Function, bypassing the slow ADP billing API call (~20s) and keeping
		// total SF execution under API Gateway's 29s timeout.
		// Non-blocking: if the org has no billing info (isValid=false) or the call fails,
		// we proceed without peCode and the SF falls back to its own GetBillingInfo step.
		setIrecBillingInfo(null);
		const fetchIrecBillingInfo = async () => {
			try {
				const { data } = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/ireceivables/billing-info${
						adminView ? '?ooid='.concat(ooid) : ''
					}`
				);
				if (data?.isValid && data?.billingInfo?.peCode) {
					setIrecBillingInfo(data.billingInfo);
				}
			} catch {
				// Non-blocking — SF will fall back to GetBillingInfo if peCode is absent
			}
		};
		fetchIrecBillingInfo();
	}, [ooid]);

	const mappedRoles = memberRoles.map((role) => {
		const val = find(roleDescriptions, {roleCode: role.roleCode});
		const newRoleName = val ? val.title : 'NA';
		return {
			roleID: role.id,
			roleCode: role.roleCode,
			roleName: newRoleName,
			roleDescription: role.roleDescription,
		};

	});
	const mappedRoles2 = memberRoles.map((role) => { return {roleId: role.id};});

	const member = {
		roles: mappedRoles2,
	};

	const createMember = async(
			dataToSend, fnameField, lnameField, getFullName) => {
		try {

			const addRoleCodes = dataToSend.roles.map(role => {
				const val = find(memberRoles, {id: role.id});
				return {...role, roleCode: val.roleCode};
			});

			const formatData = {
				countryDialing: dataToSend.countryCode,
				dialNumber: dataToSend.phone,
				emailAddress: dataToSend.email,
				firstName: dataToSend.firstName,
				lastName: dataToSend.lastName,
				preferredPhoneNumber: dataToSend.phone,
				roles: addRoleCodes,
				// Optional: pass pre-fetched peCode so the iRec provisioning SF can skip
				// the slow ADP billing API call (~20s). Only included if the org has billing info.
				...(irecBillingInfo?.peCode && { peCode: irecBillingInfo.peCode }),
			};

			const {status, data} = await axios.post(
					`${import.meta.env.VITE_ROOT_PATH}/api/members${adminView
							? '?ooid='.concat(ooid)
							: ''}`, formatData);

			if (status === 200) {
				setToastData([
					true, {
						status: 'success',
						message: `Added member ${getFullName(fnameField.value, lnameField.value)}!`
					}]);

				setMembersList(members => {
					const memberData = {
						...data,
						isLoggedUser: false,
						status: 'Active',
						roles: get(data, 'roles', []).map(role => {
							return {...role, roleId: role.id};
						}),
					};
					const tempArr = [...members, memberData];
					const ret = orderBy(tempArr,
							['status', member => member.firstName.toLowerCase()],
							['asc', 'asc']);

					return ret;
				});
				onClose();

			} else {
				setToastData([
					true, {
						status: 'error',
						message: `Unable to add member ${getFullName(fnameField.value, lnameField.value)}. Please try again!`
					}]);

				setTimeout(() => {
					onClose();
				}, 1000);
			}
		} catch (e) {
			const errorMessage = get(e, 'response.data.message', `Unable to add member ${getFullName(fnameField.value, lnameField.value)}. Please try again!`);

			setToastData([
				true, {
					status: 'error',
					message: errorMessage
				}]);

			setTimeout(() => {
				onClose();
			}, 1000);
		}
	};
	return (<>
		{isOpen && <AddEditMemberModal
				isOpen={isOpen}
				onClose={onClose}
				mode="add"
				app="pss"
				createMember={createMember}
				allRoles={mappedRoles}
				member={member}
				setToastData={setToastData}
		/>}
	</>);

}
